import { HiX, HiChat, HiLightningBolt, HiClipboardList, HiBell, HiSearch, HiTrendingUp, HiExternalLink, HiSparkles } from 'react-icons/hi'
import { Link } from 'react-router-dom'
import HalEye from '../common/HalEye'
import { useProjectContext } from '../../context/ProjectContext'

// A compact subset of quick prompts to give a taste of what AI can do
const QUICK_ACTIONS = [
  {
    icon: HiClipboardList,
    label: "View my tasks",
    color: "brand"
  },
  {
    icon: HiLightningBolt,
    label: "Create a task",
    color: "orange"
  },
  {
    icon: HiTrendingUp,
    label: "Project status",
    color: "green"
  },
  {
    icon: HiBell,
    label: "Set a reminder",
    color: "purple"
  },
  {
    icon: HiSearch,
    label: "Search tasks",
    color: "blue"
  },
]

const colorMap = {
  brand: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
}

export default function AIChatPanel({ isOpen, onClose }) {
  const { currentProject } = useProjectContext()

  if (!isOpen) return null

  return (
    <aside className="w-[280px] bg-white dark:bg-surface-800 border-l border-surface-200 dark:border-surface-700 flex flex-col shrink-0">
      {/* Header */}
      <div className="h-14 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-2">
          <HalEye size="w-8 h-8" />
          <h2 className="font-semibold text-surface-800 dark:text-surface-200 text-sm">HAL 9000</h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-surface-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 active:bg-red-100 rounded-lg transition-all duration-150"
          title="Close panel"
        >
          <HiX className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Welcome */}
        <div className="text-center pt-2 pb-4">
          <HalEye size="w-12 h-12" className="mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-surface-800 dark:text-surface-200 mb-1">
            HAL 9000
          </h3>
          <p className="text-xs text-surface-500 leading-relaxed">
            I manage your projects, track tasks, and keep everything on schedule. Open the full chat — I've been expecting you.
          </p>
        </div>

        {/* Project context */}
        {currentProject && (
          <div className="mb-4 px-3 py-2 bg-gradient-to-r from-brand-50 to-blue-50 dark:from-brand-900/30 dark:to-blue-900/30 rounded-lg border border-brand-100 dark:border-brand-800 flex items-center gap-2">
            <span 
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: currentProject.color || '#3b82f6' }}
            />
            <span className="text-xs font-medium text-brand-700 dark:text-brand-300 truncate">
              {currentProject.name}
            </span>
          </div>
        )}

        {/* Open Full Chat CTA */}
        <Link
          to="/ai-chat"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-ai-500 to-purple-600 text-white font-medium text-sm hover:from-ai-600 hover:to-purple-700 shadow-sm hover:shadow-md transition-all duration-200 mb-4"
        >
          <HiChat className="w-5 h-5" />
          Open Full Chat
          <HiExternalLink className="w-4 h-4 opacity-75" />
        </Link>

        {/* Quick action shortcuts */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-surface-400 dark:text-surface-500 uppercase tracking-wider px-1 mb-2">
            Quick Actions
          </p>
          {QUICK_ACTIONS.map((item, idx) => (
            <Link
              key={idx}
              to="/ai-chat"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700/50 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-sm transition-all duration-150 text-left group"
            >
              <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${colorMap[item.color]}`}>
                <item.icon className="w-3.5 h-3.5" />
              </div>
              <span className="text-xs text-surface-700 dark:text-surface-300 font-medium group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                {item.label}
              </span>
            </Link>
          ))}
        </div>

        {/* Features hint */}
        <div className="mt-5 p-3 bg-surface-50 dark:bg-surface-700/50 rounded-xl border border-surface-200 dark:border-surface-600">
          <p className="text-xs text-surface-500 dark:text-surface-400 leading-relaxed">
            The full AI chat includes conversation history, SharePoint integration, file imports, calendar sync, and more.
          </p>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="border-t border-surface-200 dark:border-surface-700 p-3">
        <Link
          to="/ai-chat"
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-ai-600 dark:text-ai-400 hover:bg-ai-50 dark:hover:bg-ai-900/20 transition-colors"
        >
          <HiChat className="w-4 h-4" />
          Open full chat experience
        </Link>
      </div>
    </aside>
  )
}
